import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, InputNumber, Switch, Button, Divider, Spin, Tooltip } from "antd";
import {
  fetchDIDSettings,
  saveDIDSettings,
  fetchDIDBalance,
} from "../../../core/data/redux/slices/didlogicAdminSlice";

const AdminDIDSettings = () => {
  const dispatch = useDispatch();
  const { settings, loading, saving, balance, balanceLoading, balanceError, balanceLastFetched } =
    useSelector((s) => s.didlogicAdmin);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchDIDSettings());
    dispatch(fetchDIDBalance());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      form.setFieldsValue({
        apiToken:                settings.apiToken || "",
        isTestMode:              settings.isTestMode ?? true,
        numberMarginPercent:     settings.numberMarginPercent     ?? 0,
        activationMarginPercent: settings.activationMarginPercent ?? 0,
        callMarginPercent:       settings.callMarginPercent       ?? 0,
      });
    }
  }, [settings, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      dispatch(saveDIDSettings(values));
    } catch (_) {}
  };

  const numberMargin     = Form.useWatch("numberMarginPercent",     form) || 0;
  const activationMargin = Form.useWatch("activationMarginPercent", form) || 0;
  const callMargin       = Form.useWatch("callMarginPercent",       form) || 0;

  const balanceDisplay = balance !== null && balance !== undefined
    ? `$${Number(balance).toFixed(2)}`
    : null;

  const lowBalance = balance !== null && balance !== undefined && Number(balance) < 10;

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">

        {/* Page header */}
        <div className="row align-items-center mb-4">
          <div className="col">
            <h4 className="fw-bold mb-0">DIDProvider Settings</h4>
            <p className="text-muted mb-0">Configure API credentials and pricing margins</p>
          </div>
        </div>

        {/* ── Balance banner ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 28 }}>
          <div style={{
            background: "linear-gradient(135deg, #3C2371 0%, #5B6ABF 55%, #8e77fa 100%)",
            borderRadius: 16,
            padding: "28px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 20,
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              {/* Icon */}
              <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: "rgba(255,255,255,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.12)",
              }}>
                <i className="ti ti-wallet" style={{ fontSize: 32, color: "#fff" }} />
              </div>

              <div>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, margin: 0, fontWeight: 500, letterSpacing: 0.5 }}>
                  PROVIDER ACCOUNT BALANCE
                </p>
                {balanceLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                    <Spin size="small" style={{ filter: "invert(1)" }} />
                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>Fetching balance…</span>
                  </div>
                ) : balanceError ? (
                  <div style={{ marginTop: 4 }}>
                    <span style={{ color: "#ff7875", fontSize: 16, fontWeight: 600 }}>
                      <i className="ti ti-alert-circle me-2" />Unable to fetch
                    </span>
                    <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, margin: "2px 0 0" }}>
                      {balanceError}
                    </p>
                  </div>
                ) : (
                  <div style={{ marginTop: 2 }}>
                    <span style={{
                      fontSize: 42,
                      fontWeight: 800,
                      color: lowBalance ? "#ff7875" : "#fff",
                      letterSpacing: -1,
                      lineHeight: 1.1,
                    }}>
                      {balanceDisplay ?? "—"}
                    </span>
                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, marginLeft: 6 }}>USD</span>
                    {balanceLastFetched && (
                      <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 11, margin: "4px 0 0" }}>
                        Updated {new Date(balanceLastFetched).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right side — warning + refresh */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
              {lowBalance && (
                <div style={{
                  background: "rgba(255,77,79,0.15)",
                  border: "1px solid rgba(255,77,79,0.4)",
                  borderRadius: 10,
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#ff7875",
                  fontSize: 13,
                  fontWeight: 600,
                }}>
                  <i className="ti ti-alert-triangle" />
                  Low balance — top up on DIDProvider
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <Tooltip title="Refresh balance">
                  <button
                    onClick={() => dispatch(fetchDIDBalance())}
                    disabled={balanceLoading}
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: 10,
                      padding: "8px 16px",
                      color: "rgba(255,255,255,0.7)",
                      cursor: balanceLoading ? "not-allowed" : "pointer",
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => { if (!balanceLoading) e.currentTarget.style.background = "rgba(255,255,255,0.14)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                  >
                    <i className={`ti ti-refresh ${balanceLoading ? "ti-spin" : ""}`} />
                    Refresh
                  </button>
                </Tooltip>
                <a
                  href="https://app.didlogic.com/billing"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    border: "none",
                    borderRadius: 10,
                    padding: "8px 18px",
                    color: "#3C2371",
                    fontWeight: 700,
                    fontSize: 13,
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <i className="ti ti-external-link" />
                  Top Up
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Settings form ───────────────────────────────────────────────────── */}
        <div className="row">
          <div className="col-12">
            <Form form={form} layout="vertical" onFinish={handleSave}>
              {/* API Configuration */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="ti ti-key me-2" />API Configuration
                  </h5>
                </div>
                <div className="card-body">
                  <Form.Item
                    name="apiToken"
                    label="DIDProvider API Token"
                    rules={[{ required: true, message: "API token is required" }]}
                    extra="Get your token from the DIDProvider dashboard under API settings."
                  >
                    <Input.Password placeholder="Enter your DIDProvider Bearer token" />
                  </Form.Item>

                  <Form.Item name="isTestMode" label="Test Mode" valuePropName="checked">
                    <Switch checkedChildren="Test" unCheckedChildren="Live" />
                  </Form.Item>
                  <p className="text-muted small mb-0">
                    In test mode, purchases are simulated — no real call is made to DIDProvider and no actual number is bought. Credits are still deducted and all records are created so the full flow can be tested. Switch to Live when ready to make real purchases.
                  </p>
                </div>
              </div>

              {/* Margin Settings */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="ti ti-percentage me-2" />Pricing Margins
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <Form.Item
                        name="numberMarginPercent"
                        label="Monthly Number Margin (%)"
                        extra="Added to DIDProvider's monthly rental price"
                        rules={[{ required: true, type: "number", min: 0, max: 1000 }]}
                      >
                        <InputNumber min={0} max={1000} step={0.5} precision={2} suffix="%" className="w-100" />
                      </Form.Item>
                    </div>
                    <div className="col-md-4">
                      <Form.Item
                        name="activationMarginPercent"
                        label="Activation Commission (%)"
                        extra="Added to DIDProvider's one-time activation fee"
                        rules={[{ required: true, type: "number", min: 0, max: 1000 }]}
                      >
                        <InputNumber min={0} max={1000} step={0.5} precision={2} suffix="%" className="w-100" />
                      </Form.Item>
                    </div>
                    <div className="col-md-4">
                      <Form.Item
                        name="callMarginPercent"
                        label="Call Margin (%)"
                        extra="Added to DIDProvider's per-minute call cost"
                        rules={[{ required: true, type: "number", min: 0, max: 1000 }]}
                      >
                        <InputNumber min={0} max={1000} step={0.5} precision={2} suffix="%" className="w-100" />
                      </Form.Item>
                    </div>
                  </div>

                  {/* Live Preview */}
                  <div className="bg-light rounded p-3">
                    <p className="fw-semibold mb-2">Pricing Preview</p>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <p className="small mb-1 text-muted">Monthly (DIDProvider: $1.00/mo)</p>
                        <p className="fw-bold text-success mb-0">
                          Customer pays: ${(1 * (1 + numberMargin / 100)).toFixed(2)}/mo
                        </p>
                        <p className="small text-muted">You earn: ${(1 * numberMargin / 100).toFixed(2)}/mo</p>
                      </div>
                      <div className="col-md-4">
                        <p className="small mb-1 text-muted">Activation (DIDProvider: $10.00)</p>
                        <p className="fw-bold text-success mb-0">
                          Customer pays: ${(10 * (1 + activationMargin / 100)).toFixed(2)}
                        </p>
                        <p className="small text-muted">You earn: ${(10 * activationMargin / 100).toFixed(2)}</p>
                      </div>
                      <div className="col-md-4">
                        <p className="small mb-1 text-muted">Call (DIDProvider: $0.01/min)</p>
                        <p className="fw-bold text-success mb-0">
                          Customer pays: ${(0.01 * (1 + callMargin / 100)).toFixed(4)}/min
                        </p>
                        <p className="small text-muted">You earn: ${(0.01 * callMargin / 100).toFixed(4)}/min</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Button type="primary" htmlType="submit" loading={saving} size="large">
                Save Settings
              </Button>
            </Form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDIDSettings;
